import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {TextAreaBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CreateFormInputDisplay} from '../../../../../../components/common/createFormInputs/createFormInputDisplay';
import {AsyncLoader} from '../../../../../../components/common/loaders/asyncLoader';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {Skeleton} from '../../../../../../components/common/skeleton';
import {useAppDispatch} from '../../../../../../helpers/reduxHelpers';
import {
  LocalApplicationChannelTypeComposeModesEnum,
  LocalApplicationChannelTypeReplyModesEnum,
} from '../../../../../../models/api/localApplicationChannelTypeModel';
import {LocalApplicationModel} from '../../../../../../models/api/localApplicationModel';
import {LocalApplicationServerModel} from '../../../../../../models/api/localApplicationServerModel';
import {buildSettingsDevelopersPath} from '../../../../../../models/url/settingsUrlHelpers';
import {
  SettingsDevelopersPagesEnum,
  SettingsRootPagesEnum,
} from '../../../../../../models/url/settingsUrlModel';
import {
  createLocalApplicationChannelTypeAsyncAction,
  fetchLocalApplicationChannelTypeAsyncAction,
  fetchLocalApplicationServersAsyncAction,
  updateLocalApplicationChannelTypeAsyncAction,
} from '../../../../../../store/data/localApplications/localApplicationsActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../../../../banners/settingsBannersContext';
import {SettingsDevelopersChannelBooleanInput} from './settingsDeveloperChannelBooleanInput';
import {SettingsDevelopersChannelComposerTypeInput} from './settingsDevelopersChannelComposerTypeInput';
import {SettingsDevelopersChannelContactTypeInput} from './settingsDevelopersChannelContactTypeInput';
import {SettingsDevelopersChannelWebhookUrlInput} from './settingsDevelopersChannelWebhookUrlInput';

/*
 * Props.
 */

interface SettingsDevelopersApplicationFeatureChannelProps {
  application: LocalApplicationModel;
  servers?: ReadonlyArray<LocalApplicationServerModel>;
  serversAsyncStatus?: AsyncStatusesEnum;
}

/*
 * Style.
 */

const StyledChannelWrapperDiv = styled.div`
  max-width: 600px;
  width: 100%;
  padding: 0 1px 64px 1px;
  overflow: auto;
`;

const StyledErrorWrapperDiv = styled.div`
  flex: 1;
  padding: 32px;
`;

const StyledWarningBanner = styled.div`
  display: flex;
  flex-flow: row;
  background: ${(p) => p.theme.palette.orange.shade20};
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  align-items: center;
  margin-bottom: 28px;
`;

const StyledBooleanInputsWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
`;

const StyledSettingsDevelopersChannelBooleanInput = styled(SettingsDevelopersChannelBooleanInput)`
  margin-bottom: 8px;
`;

const StyledFooterDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.alphas.gray10};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 8px;
  background: ${(p) => p.theme.greys.white};
`;

/*
 * Component.
 */

export const SettingsDevelopersApplicationFeatureChannel: FC<
  SettingsDevelopersApplicationFeatureChannelProps
> = (props) => {
  const {application, servers, serversAsyncStatus} = props;
  const {channelType} = application;
  const dispatch = useAppDispatch();
  const theme = useUserTheme();
  const {navigateToLink} = useLinkNavigation();
  const {showBanner} = useSettingsBanners();

  const [description, setDescription] = useState(channelType?.value?.description || '');
  const [webhookOrigin, setWebhookOrigin] = useState(computeWebhookOrigin(channelType?.value?.webhookUrl));
  const [webhookPath, setWebhookPath] = useState(computeWebhookPath(channelType?.value?.webhookUrl));
  const [isWebhookUrlValid, setIsWebhookUrlValid] = useState(false);
  const [contactType, setContactType] = useState(channelType?.value?.settings?.contactType);
  const [shouldAllowReplying, setShouldAllowReplying] = useState(
    computeShouldAllowReplying(channelType?.value?.settings?.replyMode),
  );
  const [shouldAllowComposing, setShouldAllowComposing] = useState(
    computeShouldAllowComposing(channelType?.value?.settings?.composeMode),
  );
  const [composerType, setComposerType] = useState(channelType?.value?.settings?.composerType);

  const [isSaving, setIsSaving] = useState(false);

  const isFormValid = Boolean(description.trim() !== '' && isWebhookUrlValid && contactType && composerType);

  // Make sure we have the latest server values.
  useInitialEffect(() => {
    dispatch(fetchLocalApplicationServersAsyncAction({localApplicationId: application.id}));
  });

  // Watch the local applications channelType value, if it changes update the values.
  useEffect(() => {
    setDescription(channelType?.value?.description || '');
    setWebhookOrigin(computeWebhookOrigin(channelType?.value?.webhookUrl));
    setWebhookPath(computeWebhookPath(channelType?.value?.webhookUrl));
    setContactType(channelType?.value?.settings?.contactType);
    setShouldAllowReplying(computeShouldAllowReplying(channelType?.value?.settings?.replyMode));
    setShouldAllowComposing(computeShouldAllowComposing(channelType?.value?.settings?.composeMode));
    setComposerType(channelType?.value?.settings?.composerType);
  }, [channelType?.value]);

  const navigateBackToFeatures = () => {
    navigateToLink(
      buildSettingsDevelopersPath({
        rootPage: SettingsRootPagesEnum.DEVELOPERS,
        subPage: SettingsDevelopersPagesEnum.APPLICATION_FEATURES,
        applicationId: application.id,
      }),
    );
  };

  const onSaveClick = async () => {
    if (!isFormValid || !contactType || !composerType) {
      return;
    }

    setIsSaving(true);
    const composeMode = shouldAllowComposing
      ? LocalApplicationChannelTypeComposeModesEnum.NORMAL
      : LocalApplicationChannelTypeComposeModesEnum.UNSUPPORTED;
    const replyMode = shouldAllowReplying
      ? LocalApplicationChannelTypeReplyModesEnum.SAME_CHANNEL
      : LocalApplicationChannelTypeReplyModesEnum.UNSUPPORTED;

    if (application.channelType) {
      try {
        await dispatch(
          updateLocalApplicationChannelTypeAsyncAction(application.id, application.channelType.id, {
            description,
            webhookUrl: webhookOrigin + webhookPath,
            settings: {
              composeMode,
              composerType,
              replyMode,
              // TODO: support this when we figure out how we want to format it.
              supportedAttachmentTypes: [],
              // This is always set to try currently.
              supportsImportedMessageNotifications: true,
            },
          }),
        );
        navigateBackToFeatures();
      } catch {
        setIsSaving(false);
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: (
            <Trans id="developers-application-feature-channel-error-updating-message">
              Error updating channel type. Please try again.
            </Trans>
          ),
        });
      }
    } else {
      try {
        await dispatch(
          createLocalApplicationChannelTypeAsyncAction(application.id, {
            description,
            webhookUrl: webhookOrigin + webhookPath,
            settings: {
              composeMode,
              composerType,
              contactType,
              replyMode,
              // TODO: support this when we figure out how we want to format it.
              supportedAttachmentTypes: [],
              // This is always set to try currently.
              supportsImportedMessageNotifications: true,
            },
          }),
        );
        navigateBackToFeatures();
      } catch {
        setIsSaving(false);
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: (
            <Trans id="developers-application-feature-channel-error-creating-message">
              Error creating channel type. Please try again.
            </Trans>
          ),
        });
      }
    }
  };

  // TODO: Flesh out a better loading state.
  if (
    !servers ||
    serversAsyncStatus === AsyncStatusesEnum.LOADING ||
    // If we should have a channel type value and currently are loading it.
    (channelType && (!channelType?.status || channelType?.status === AsyncStatusesEnum.LOADING))
  ) {
    return <Skeleton height="300px" width="600px" />;
  }
  // If we failed to fetch the servers.
  if (serversAsyncStatus === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorWrapperDiv>
        <AsyncLoader
          status={AsyncStatusesEnum.FAILED}
          onRetryClick={() =>
            dispatch(fetchLocalApplicationServersAsyncAction({localApplicationId: application.id}))
          }
        />
      </StyledErrorWrapperDiv>
    );
  }
  if (channelType && channelType.status === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorWrapperDiv>
        <AsyncLoader
          status={AsyncStatusesEnum.FAILED}
          onRetryClick={() =>
            dispatch(fetchLocalApplicationChannelTypeAsyncAction(application.id, channelType.id))
          }
        />
      </StyledErrorWrapperDiv>
    );
  }

  return (
    <StyledChannelWrapperDiv>
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-feature-channel-description">Short description</Trans>}
        input={
          <TextAreaBox
            value={description}
            onChange={(value) => setDescription(value)}
            isRequired
            noMargin
            message={
              <Trans id="developers-application-feature-channel-description-message">
                A brief summary of the of the channel&apos;s use case
              </Trans>
            }
          />
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-feature-channel-webhook-url">Webhook URL</Trans>}
        input={
          <SettingsDevelopersChannelWebhookUrlInput
            application={application}
            servers={servers}
            webhookUrlOrigin={webhookOrigin}
            webhookUrlPath={webhookPath}
            onWebhookUrlOriginChange={setWebhookOrigin}
            onWebhookUrlPathChange={setWebhookPath}
            onWebhookUrlValidationChange={setIsWebhookUrlValid}
          />
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-feature-channel-contact-type">Contact type</Trans>}
        input={
          <SettingsDevelopersChannelContactTypeInput
            selectedContactType={contactType}
            onContactTypeChange={setContactType}
            isDisabled={Boolean(channelType)}
          />
        }
      />
      <StyledWarningBanner>
        <Icon name="warningFilledSmall" color={theme.palette.orange.shade50} />
        <Trans id="developers-application-feature-channel-contact-type-warning-banner">
          The Contact type cannot be changed after creating the channel.
        </Trans>
      </StyledWarningBanner>
      <CreateFormInputDisplay
        title={<Trans id="developers-application-feature-channel-allow-options-title">Allow</Trans>}
        input={
          <StyledBooleanInputsWrapperDiv>
            <SettingsDevelopersChannelBooleanInput
              isChecked={shouldAllowReplying}
              onChange={setShouldAllowReplying}
              title={<Trans id="developers-application-feature-channel-allow-replying-title">Reply</Trans>}
              description={
                <Trans id="developers-application-feature-channel-allow-replying-description">
                  Allow users to reply to messages they receive through your channel
                </Trans>
              }
            />
            <StyledSettingsDevelopersChannelBooleanInput
              isChecked={shouldAllowComposing}
              onChange={setShouldAllowComposing}
              title={<Trans id="developers-application-feature-channel-allow-composing-title">Compose</Trans>}
              description={
                <Trans id="developers-application-feature-channel-allow-composing-description">
                  Allow users to compose new outbound messages through your channel
                </Trans>
              }
            />
          </StyledBooleanInputsWrapperDiv>
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-feature-channel-composer-type">Composer type</Trans>}
        tooltip={
          <Trans id="developers-application-feature-channel-composer-type-tooltip">
            SMS and most live chat channels should use the Basic composer, but the Advanced composer is
            recommended if any text formatting is required.
          </Trans>
        }
        input={
          <SettingsDevelopersChannelComposerTypeInput
            selectedComposerType={composerType}
            onComposerTypeChange={setComposerType}
          />
        }
      />
      <StyledFooterDiv>
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={navigateBackToFeatures} isDisabled={isSaving}>
            <Trans id="developers-application-server-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            isDisabled={!isFormValid || isSaving}
            onClick={onSaveClick}
          >
            {!application.channelType && (
              <Trans id="developers-application-server-create-button">Create</Trans>
            )}
            {application.channelType && (
              <Trans id="developers-application-server-update-button">Update</Trans>
            )}
          </Button>
        </ButtonGroup>
      </StyledFooterDiv>
    </StyledChannelWrapperDiv>
  );
};

/*
 * Helpers.
 */

function computeWebhookOrigin(webhookUrl?: string) {
  if (!webhookUrl) {
    return '';
  }
  return new URL(webhookUrl).origin;
}

function computeWebhookPath(webhookUrl?: string) {
  if (!webhookUrl) {
    return '';
  }
  return new URL(webhookUrl).pathname;
}

function computeShouldAllowReplying(mode?: LocalApplicationChannelTypeReplyModesEnum) {
  // If we do not have a mode, we should default to true.
  if (!mode) {
    return true;
  }
  return mode === LocalApplicationChannelTypeReplyModesEnum.SAME_CHANNEL;
}

function computeShouldAllowComposing(mode?: LocalApplicationChannelTypeComposeModesEnum) {
  // If we do not have a mode, we should default to true.
  if (!mode) {
    return true;
  }
  return mode === LocalApplicationChannelTypeComposeModesEnum.NORMAL;
}
